/**
 * Live call intake — what the AI has understood about the call in progress.
 *
 * Routed through the operations proxy at `/api/voice/call-intake`, which holds
 * the service key for ai-operations (internal, service-only). The browser sends
 * the transcript segments it is ALREADY streaming for this call, and gets the
 * extracted intake back in the same response — so the push doubles as the read
 * and the panel never has to poll.
 */

import { apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';

/** Which side of the call spoke — Telnyx's track, forwarded unchanged. */
export type TranscriptTrack = 'inbound' | 'outbound' | string;

export interface IntakeSegment {
  /** Index in this call's transcript; the SSE event id the HUD already has. */
  seq: number;
  text: string;
  track?: TranscriptTrack | null;
}

/** Why a field says what it says — rendered next to the value. */
export interface IntakeEvidence {
  source: 'ai' | 'agent';
  /** Null for an agent edit: a human answer has no transcript to cite. */
  confidence: number | null;
  quote: string | null;
  segmentSeq: number | null;
  at: string;
}

export interface CallIntakeFields {
  customerName: string | null;
  email: string | null;
  address: string | null;
  jobTypeId: string | null;
  jobTypeName: string | null;
  issueSummary: string | null;
  urgency: string | null;
  preferredTime: string | null;
  accessNotes: string | null;
  poNumber: string | null;
}

/**
 * The address in the shape the booking form needs, split by the model that had
 * the transcript in front of it.
 *
 * Null where the caller said nothing. Preferred over parsing `fields.address`
 * here: a spoken address has no reliable commas to split on, and a parser that
 * tries files postcodes as cities.
 */
export interface CallIntakeAddressParts {
  street: string | null;
  unit: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

/**
 * One of the tenant's OWN job fields, answered from the call.
 *
 * `fieldKey` is what the Add Job form keys its custom-field inputs by, carried
 * from the backend so the form can fill an answer without correlating
 * definition ids against a schema it loaded separately.
 */
export interface IntakeCustomFieldAnswer {
  id: string;
  fieldKey: string | null;
  /** The label as ASKED, so a renamed field still reads right. */
  label: string;
  type?: IntakeCustomFieldType;
  /** The customer's words, for a TEXT field. Null on a choice field. */
  text?: string | null;
  /**
   * The option KEYS chosen — what the form stores and the values API accepts.
   *
   * Keys, not labels, and this is the whole reason the answer is three fields
   * rather than one. A field configured as `{ key: 'gas', label: 'Gas' }` is
   * answered "Gas" by a customer and stored as `gas`; writing the label into
   * the form leaves the select blank and the save rejected.
   */
  optionKeys?: string[];
  /** The answer as a person reads it — the text, or the labels chosen. */
  display?: string;
  /**
   * @deprecated The shape before the answer was split three ways. Still read so
   * a frontend deployed ahead of ai-operations keeps filling custom fields.
   */
  value?: string;
  confidence: number;
  quote: string;
}

/** The field types a phone call can answer — the rest stay with the agent. */
export type IntakeCustomFieldType = 'TEXT' | 'DROPDOWN' | 'MULTI_SELECT';

/** One of the tenant's own job fields the call has NOT answered. */
export interface IntakeCustomFieldAsk {
  id: string;
  fieldKey: string | null;
  label: string;
  type: IntakeCustomFieldType;
  description?: string | null;
  /** Key AND label: the key is what gets stored, the label is what is read. */
  options?: Array<{ key: string; label: string }>;
}

export interface CallIntake {
  callControlId: string;
  direction: string | null;
  customerPhone: string | null;
  fields: CallIntakeFields;
  /** Per-field support, present only for fields the model reported. */
  evidence?: Record<string, IntakeEvidence>;
  /** The same address as `fields.address`, split for the form. */
  addressParts?: CallIntakeAddressParts | null;
  /** Fields the agent edited — the AI proposes but never overwrites these. */
  lockedFields: string[];
  /** What the AI still needs, so the agent knows what to ask next. */
  missingFields: string[];
  /** The tenant's own job fields this call answered. */
  customFields?: IntakeCustomFieldAnswer[];
  /** The tenant's own job fields still unanswered, for the same reason. */
  unansweredCustomFields?: IntakeCustomFieldAsk[];
  segmentCount: number;
  extractionCount: number;
  lastExtractedAt: string | null;
  updatedAt: string;
}

const BASE = '/api/voice/call-intake';

export const callIntakeApi = {
  /** Push segments and get the current intake back in one round trip. */
  push: (
    callControlId: string,
    body: {
      segments: IntakeSegment[];
      direction?: string | null;
      customerPhone?: string | null;
      final?: boolean;
    },
  ) =>
    apiPost<CallIntake, typeof body>(
      operationsClient,
      `${BASE}/${encodeURIComponent(callControlId)}/segments`,
      body,
    ),

  /** The intake as it stands — used by the booking form, which pushes nothing. */
  get: (callControlId: string) =>
    apiGet<CallIntake>(operationsClient, `${BASE}/${encodeURIComponent(callControlId)}`),

  /** Record the agent's correction; those fields stop being AI-writable. */
  update: (callControlId: string, edits: Partial<Omit<CallIntakeFields, 'jobTypeName'>>) =>
    apiPatch<CallIntake, typeof edits>(
      operationsClient,
      `${BASE}/${encodeURIComponent(callControlId)}`,
      edits,
    ),
};
